import React, {useCallback} from 'react';
import {
  Dialog as DialogMui,
  DialogContent,
  DialogTitle,
  styled,
  Box,
} from '@mui/material';
import {Button} from '@lightricks/react-design-system';
import {media} from '@/styles/mediaQueries';
import translate from '@/utils/translate';
import ConnectSocialAccountPanel from './ConnectSocialAccountPanel';
import CloseModalIcon from '@/components/icons/CloseModal';
import LEGACY_COLORS from '@/styles/legacyColors';
import {scrollbarMixins} from '@/styles/mixins';

const StyledDialog = styled(DialogMui)(() => ({
  '.MuiPaper-root': {
    width: '50%',
    maxWidth: '900px',
    borderRadius: '20px',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    margin: '0',
    boxShadow: '1px 1px 10px 0 #2b2b2b !important',
    [media.mobile]: {
      width: '100%',
      maxWidth: '100%',
      height: '74vh',
      borderRadius: '0 !important',
      borderTopLeftRadius: '16px !important',
      borderTopRightRadius: '16px !important',
      padding: '30px 10px',
    },
  },
  '.MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.4) !important',
  },
  '.MuiDialog-container': {
    [media.mobile]: {
      alignItems: 'flex-end',
    },
  },
}));

const StyledDialogContainer = styled(Box)(() => ({
  padding: '40px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'hidden',
  [media.mobile]: {
    padding: '0',
    overflow: 'scroll',
  },
}));

const StyledDialogHeader = styled(DialogTitle)(() => ({
  padding: '0',
}));

const StyledDialogTitle = styled(Box)(() => ({
  fontSize: '22px',
  lineHeight: '1.2',
  [media.mobile]: {
    fontSize: '18px',
  },
}));

const StyledCloseButton = styled(Button)(() => ({
  position: 'absolute',
  top: '40px',
  right: '40px',
  padding: '0',
  height: '20px',
  [media.mobile]: {
    transform: 'scale(0.7)',
    position: 'absolute',
    top: '30px',
  },
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  padding: '40px 70px 0 70px !important',
  ...scrollbarMixins.overrideScrollbarHidden(),

  [media.mobile]: {
    padding: '10px 0 !important',
  },
}));

export interface ConnectSocialAccountPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * ConnectSocialAccountPopup - Modal wrapper for the ConnectSocialAccountPanel component
 *
 * This component provides a modal interface around the standalone ConnectSocialAccountPanel component.
 */
function ConnectSocialAccountPopup({
  isOpen,
  onClose,
}: ConnectSocialAccountPopupProps) {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <StyledDialog open={isOpen} onClose={handleClose}>
      <StyledDialogContainer>
        <StyledDialogHeader>
          <StyledDialogTitle>
            {translate('Connect social account')}
          </StyledDialogTitle>
          <StyledCloseButton
            appearance="neutral"
            mode="plain"
            size="medium"
            onClick={handleClose}
          >
            <CloseModalIcon
              width={20}
              height={20}
              color={LEGACY_COLORS.GRAY_MEDIUM}
            />
          </StyledCloseButton>
        </StyledDialogHeader>

        <StyledDialogContent>
          <ConnectSocialAccountPanel />
        </StyledDialogContent>
      </StyledDialogContainer>
    </StyledDialog>
  );
}

export default ConnectSocialAccountPopup;
